import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, ChevronRight, ChevronLeft, PartyPopper, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { LOCATIONS, EXTRAS, computeDays, formatDateTimeTR, buildWhatsAppUrl, bookingMessage } from "@/lib/site";
import { useSettings } from "@/context/SettingsContext";

function nowLocal() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

const STEP_LABELS = ["Tarih & Konum", "Ek Hizmetler", "Bilgileriniz", "Onay"];

export default function BookingDialog({ open, onOpenChange, vehicle, initial = {} }) {
  const { settings } = useSettings();
  const minDate = nowLocal();
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    pickup: "Taksim", dropoff: "Taksim", pickup_dt: "", return_dt: "",
    extras: [], full_name: "", phone: "", email: "", flight_no: "", note: "",
    kvkk: false, marketing: false,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setStep(0);
      setResult(null);
      setData((d) => ({
        ...d,
        pickup: initial.pickup || "Taksim",
        dropoff: initial.dropoff || initial.pickup || "Taksim",
        pickup_dt: initial.pickup_dt || "",
        return_dt: initial.return_dt || "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const days = computeDays(data.pickup_dt, data.return_dt);

  const toggleExtra = (id) =>
    set("extras", data.extras.includes(id) ? data.extras.filter((x) => x !== id) : [...data.extras, id]);

  const validateStep = () => {
    const err = {};
    if (step === 0) {
      if (!data.pickup_dt) err.pickup_dt = "Alış tarihi gerekli";
      if (!data.return_dt) err.return_dt = "İade tarihi gerekli";
      if (data.pickup_dt && data.pickup_dt < minDate) err.pickup_dt = "Geçmiş tarih seçilemez";
      if (data.pickup_dt && data.return_dt && data.return_dt <= data.pickup_dt) err.return_dt = "İade, alıştan sonra olmalı";
    }
    if (step === 2) {
      if (!data.full_name.trim()) err.full_name = "Ad soyad gerekli";
      if (!data.phone.trim()) err.phone = "Telefon gerekli";
      if (data.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email)) err.email = "Geçerli e-posta girin";
      if (!data.kvkk) err.kvkk = "KVKK metnini onaylamalısınız";
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(3, s + 1)); };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        vehicle_id: vehicle?.id,
        vehicle_name: vehicle?.name || "Araç",
        pickup_location: data.pickup,
        dropoff_location: data.dropoff,
        pickup_datetime: data.pickup_dt,
        return_datetime: data.return_dt,
        days,
        extras: data.extras.map((id) => { const e = EXTRAS.find((x) => x.id === id); return { id: e.id, label: e.label, price: 0 }; }),
        estimated_total: 0,
        full_name: data.full_name,
        phone: data.phone,
        email: data.email || null,
        flight_no: data.flight_no || null,
        note: data.note || null,
        kvkk_accepted: data.kvkk,
        marketing_accepted: data.marketing,
      };
      const res = await api.post("/bookings", payload);
      setResult(res.data);
      toast.success("Rezervasyon talebiniz alındı!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bir hata oluştu, tekrar deneyin.");
    } finally {
      setSubmitting(false);
    }
  };

  const waUrl = result
    ? buildWhatsAppUrl(settings.whatsapp, bookingMessage({ vehicleName: vehicle?.name, pickup: formatDateTimeTR(data.pickup_dt), ret: formatDateTimeTR(data.return_dt), location: data.pickup }) + ` Talep No: ${result.code}.`)
    : "#";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="booking-dialog">
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">{vehicle?.name} — Rezervasyon Talebi</DialogTitle>
              <DialogDescription>Ön ödemesiz rezervasyon talebi oluşturun; ekibimiz sizinle iletişime geçsin.</DialogDescription>
            </DialogHeader>

            <div className="flex items-center gap-1 my-2">
              {STEP_LABELS.map((label, i) => (
                <div key={i} className="flex items-center flex-1 last:flex-none">
                  <div className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ${i <= step ? "bg-[#A11016] text-white" : "bg-neutral-200 text-neutral-500"}`}>
                    {i < step ? <Check className="h-4 w-4" /> : i + 1}
                  </div>
                  {i < STEP_LABELS.length - 1 && <div className={`h-0.5 flex-1 mx-1 ${i < step ? "bg-[#A11016]" : "bg-neutral-200"}`} />}
                </div>
              ))}
            </div>
            <p className="text-sm font-medium text-neutral-500 -mt-1">{STEP_LABELS[step]}</p>

            {step === 0 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm mb-1.5 block">Alış konumu</Label>
                    <Select value={data.pickup} onValueChange={(v) => set("pickup", v)}>
                      <SelectTrigger data-testid="booking-pickup-location"><SelectValue /></SelectTrigger>
                      <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm mb-1.5 block">Teslim konumu</Label>
                    <Select value={data.dropoff} onValueChange={(v) => set("dropoff", v)}>
                      <SelectTrigger data-testid="booking-dropoff-location"><SelectValue /></SelectTrigger>
                      <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">Alış tarihi ve saati</Label>
                  <Input type="datetime-local" min={minDate} value={data.pickup_dt} onChange={(e) => set("pickup_dt", e.target.value)} data-testid="booking-pickup-datetime" />
                  {errors.pickup_dt && <p className="text-xs text-[#A11016] mt-1">{errors.pickup_dt}</p>}
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">İade tarihi ve saati</Label>
                  <Input type="datetime-local" min={data.pickup_dt || minDate} value={data.return_dt} onChange={(e) => set("return_dt", e.target.value)} data-testid="booking-return-datetime" />
                  {errors.return_dt && <p className="text-xs text-[#A11016] mt-1">{errors.return_dt}</p>}
                </div>
                {data.pickup_dt && data.return_dt && !errors.return_dt && (
                  <div className="rounded-lg bg-[#F5F5F5] p-3 text-sm text-neutral-700">Tahmini süre: <b>{days} gün</b></div>
                )}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-2">
                {EXTRAS.map((e) => {
                  const active = data.extras.includes(e.id);
                  return (
                    <button key={e.id} type="button" onClick={() => toggleExtra(e.id)} data-testid={`booking-extra-${e.id}`}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border transition-colors ${active ? "border-[#A11016] bg-[#FFF0F2]" : "border-neutral-200 hover:border-neutral-300"}`}>
                      <div className="flex items-center gap-3">
                        <Checkbox checked={active} className="pointer-events-none" />
                        <span className="text-sm font-medium text-neutral-800">{e.label}</span>
                      </div>
                    </button>
                  );
                })}
                <p className="text-xs text-neutral-400 pt-1">Ek hizmet ücretleri rezervasyon onayı sırasında size bildirilir.</p>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm mb-1.5 block">Ad Soyad *</Label>
                  <Input value={data.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="booking-fullname" />
                  {errors.full_name && <p className="text-xs text-[#A11016] mt-1">{errors.full_name}</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm mb-1.5 block">Telefon *</Label>
                    <Input value={data.phone} onChange={(e) => set("phone", e.target.value)} placeholder="05xx xxx xx xx" data-testid="booking-phone" />
                    {errors.phone && <p className="text-xs text-[#A11016] mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <Label className="text-sm mb-1.5 block">E-posta</Label>
                    <Input value={data.email} onChange={(e) => set("email", e.target.value)} data-testid="booking-email" />
                    {errors.email && <p className="text-xs text-[#A11016] mt-1">{errors.email}</p>}
                  </div>
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">Uçuş numarası (isteğe bağlı)</Label>
                  <Input value={data.flight_no} onChange={(e) => set("flight_no", e.target.value)} data-testid="booking-flight" />
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">Not (isteğe bağlı)</Label>
                  <Textarea value={data.note} onChange={(e) => set("note", e.target.value)} rows={2} data-testid="booking-note" />
                </div>
                <div className="flex items-start gap-2">
                  <Checkbox id="kvkk" checked={data.kvkk} onCheckedChange={(v) => set("kvkk", !!v)} data-testid="booking-kvkk" className="mt-0.5" />
                  <Label htmlFor="kvkk" className="text-xs text-neutral-600 cursor-pointer leading-relaxed">KVKK Aydınlatma Metni'ni okudum ve kişisel verilerimin işlenmesini kabul ediyorum. *</Label>
                </div>
                {errors.kvkk && <p className="text-xs text-[#A11016]">{errors.kvkk}</p>}
                <div className="flex items-start gap-2">
                  <Checkbox id="marketing" checked={data.marketing} onCheckedChange={(v) => set("marketing", !!v)} data-testid="booking-marketing" className="mt-0.5" />
                  <Label htmlFor="marketing" className="text-xs text-neutral-600 cursor-pointer leading-relaxed">Kampanya ve fırsatlardan haberdar olmak için ticari ileti almayı kabul ediyorum. (isteğe bağlı)</Label>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-3 text-sm">
                <div className="rounded-xl border border-neutral-200 divide-y">
                  <Row l="Araç" v={vehicle?.name} />
                  <Row l="Alış" v={`${data.pickup} • ${formatDateTimeTR(data.pickup_dt)}`} />
                  <Row l="İade" v={`${data.dropoff} • ${formatDateTimeTR(data.return_dt)}`} />
                  <Row l="Süre" v={`${days} gün`} />
                </div>
                <p className="text-xs text-neutral-400">Bu bir ön ödemesiz rezervasyon talebidir. Ekibimiz sizinle iletişime geçerek talebinizi ve güncel fiyatı netleştirecektir.</p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              {step > 0 && <Button variant="outline" onClick={prev} data-testid="booking-prev-button" className="gap-1"><ChevronLeft className="h-4 w-4" /> Geri</Button>}
              {step < 3 ? (
                <Button onClick={next} data-testid="booking-next-button" className="flex-1 bg-[#A11016] hover:bg-[#820D12] text-white gap-1">Devam Et <ChevronRight className="h-4 w-4" /></Button>
              ) : (
                <Button onClick={submit} disabled={submitting} data-testid="booking-submit-button" className="flex-1 bg-[#A11016] hover:bg-[#820D12] text-white">{submitting ? "Gönderiliyor..." : "Talebi Gönder"}</Button>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-4" data-testid="booking-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-[#FFF0F2] flex items-center justify-center mb-4">
              <PartyPopper className="h-8 w-8 text-[#A11016]" />
            </div>
            <h3 className="text-xl font-bold text-[#171717]">Talebiniz Alındı!</h3>
            <p className="text-sm text-neutral-600 mt-2">Rezervasyon talebiniz başarıyla oluşturuldu. En kısa sürede sizinle iletişime geçeceğiz.</p>
            <div className="my-5 inline-flex items-center gap-2 bg-[#F5F5F5] rounded-xl px-4 py-3">
              <span className="text-sm text-neutral-500">Talep No:</span>
              <span className="text-lg font-bold text-[#A11016] tracking-wider" data-testid="booking-code">{result.code}</span>
              <button onClick={() => { navigator.clipboard?.writeText(result.code); toast.success("Kopyalandı"); }} className="text-neutral-400 hover:text-[#A11016]"><Copy className="h-4 w-4" /></button>
            </div>
            <div className="flex flex-col gap-2">
              <a href={waUrl} target="_blank" rel="noreferrer" data-testid="booking-whatsapp-button">
                <Button className="w-full bg-[#25D366] hover:bg-[#1EBE5D] text-white">WhatsApp ile Gönder</Button>
              </a>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="booking-close-button">Kapat</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ l, v }) => (
  <div className="flex justify-between p-3">
    <span className="text-neutral-500">{l}</span>
    <span className="font-medium text-[#171717] text-right">{v}</span>
  </div>
);
